import { io, Socket } from 'socket.io-client';
import { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useUserValidation } from '../hooks/useUserValidation';
import { SOCKET_URL } from '../utils/constants';
const SOCKET_HOST = SOCKET_URL;

interface AppContextProps {
    role?: string;
    userEmail?: string;
    jointToChatRoom: (data: ChatJoinPayload) => void;
    logout: () => void;
    clearData: () => void;
    socketRef: React.MutableRefObject<Socket | null>;
    tool: Tool;
    setTool: (newTool: Tool) => void;
}

export type ChatJoinPayload = {
    role?: string;
    userEmail: string;
    name?: string;
    tool: Tool;
};

export enum Tool {
    ETF='ETF',
    SCC='SCC',
}

const AppContext = createContext<AppContextProps | undefined>(undefined);


export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {

    
    const isInitializedRef = useRef(false);

    const { isValideUser } = useUserValidation();
    const [role, setRole] = useState<string>();
    const [userEmail, setUserEmail] = useState<string>();
    const [tool, setTool] = useState<Tool>(Tool.ETF);

    const socketRef = useRef<Socket | null>(null);

    useEffect(() => {
        const userEmail = sessionStorage.getItem('userEmail');
        const tool = sessionStorage.getItem('tool');       
        if (!isInitializedRef.current){
            isInitializedRef.current = true;
            if (!socketRef.current) {
                if (userEmail) {
                    const body: ChatJoinPayload = {
                        userEmail: userEmail,
                        role: 'user',
                        tool: tool? tool as Tool : Tool.ETF,
                    };
                    startJointToChatRoom(body);
                }
    
                const csiEmail = sessionStorage.getItem('csiEmail');
                if (csiEmail) {
                    const body: ChatJoinPayload = {
                        userEmail: csiEmail,
                        role: 'csi',
                        tool: tool? tool as Tool : Tool.ETF,
                    };
                    startJointToChatRoom(body);
                }
            }
        }

    }, []);



    const startJointToChatRoom  = (data: ChatJoinPayload) => {
        if (!socketRef.current) {
            if(data.role === 'csi'){
                jointToChatRoom(data);
            }else{
                isValideUser(data.userEmail, data.tool)
                .then(isValid => {
                    if(!isValid){
                        throw new Error('Invalid user or tool');
                    }
                    jointToChatRoom(data);
                })
                .catch(err => {
                    console.error(err);
                })
            }
        }
    };

    const jointToChatRoom  = (data: ChatJoinPayload) => {
        console.log('Connecting to socket server...', data);            
        const socket = io(SOCKET_HOST, { transports: ['websocket'] });
        socket.on('connect', () => {
            console.log('Connected to socket server', socket.id);
            
            socketRef.current = socket;
            setUserEmail(data.userEmail);
            setTool(data.tool);
            setRole(data.role);
            socket.emit('user:online', { userId: data.userEmail, tool: data.tool, name: data.name });
            sessionStorage.setItem('tool', data.tool);
            sessionStorage.setItem('role', data.role || '');
            if(data.role === 'csi'){
                sessionStorage.setItem('csiEmail', data.userEmail);
            }else{
                sessionStorage.setItem('userEmail', data.userEmail);
            }
        });
    };

    const logout = () => {
        clearData();
    };

    const clearData = () => {
        setRole(undefined);
        setUserEmail(undefined);
        console.log("Clearing session storage");
        sessionStorage.clear();
        if (socketRef.current) {
            socketRef.current.disconnect();
            socketRef.current = null;            
        }
    };

    const setCurrentTool = (newTool: Tool) => {
        setTool(newTool);
        sessionStorage.setItem('tool', newTool);        
    };

    return (
        <AppContext.Provider value={{
            role: role,
            userEmail: userEmail,  
            jointToChatRoom: startJointToChatRoom,
            socketRef: socketRef,
            tool: tool,
            logout: logout,
            clearData: clearData,
            setTool: setCurrentTool
        }}>
            {children}
        </AppContext.Provider>
    );
}

export const useAppDate = (): AppContextProps => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
};